import { EControlType } from "components/ControlWrapper";
import { friendlyValueToFlexibleFieldValue } from "global/Application";
import { IFlexibleField, IFlexibleFieldValue, IGroupConfigurationConfig, ITierLevel, TValue } from "model/FlexibleAttributeModel";
import { IFlexibleFormDefinition } from "model/FlexibleFormModel";
import { v4 as uuidv4 } from 'uuid';
import { $cat_$accounts_receivable_id, $cat_$fees_and_premiums_id, $cat_$group_configuration_id, $cat_$group_number_id, $cat_$plan_id, $cat_$stop_loss_id } from "model/ConfigCategoryConstants";

export class FlexibleFieldGenerator {
    private readonly release: IGroupConfigurationConfig;

    constructor(release: IGroupConfigurationConfig) {
        this.release = release;
    }

    private generateFieldsForFlexibleFormDefinition(flexibleFormDefinition: IFlexibleFormDefinition, tiers?: ITierLevel[] | null): IFlexibleField[] {
        let fields: IFlexibleField[] = [];

        for (let tab of flexibleFormDefinition.tabs) {
            for (let section of tab.sections) {
                for (let row of section.rows) {
                    for (let attr of row.attributes) {
                        let values: IFlexibleFieldValue[] = [];

                        // NOTE: we can only have PCP/Specialist splits when it's tier enabled
                        let generateTiers = attr.bcbs_config_attribute.bcbs_istierenabled && tiers && tiers.length > 0;
                        let generatePcpSpecialist = attr.bcbs_config_attribute.bcbs_isseparatepcpandspecialist;

                        // ignore
                        let defaultValue = null as TValue | null;
                        if (attr.bcbs_config_attribute.bcbs_defaultvalue)
                            if (attr.bcbs_config_masterattribute.bcbs_attributetypecode !== EControlType.message)
                                defaultValue = friendlyValueToFlexibleFieldValue(
                                    this.release,
                                    attr.bcbs_config_attribute.bcbs_defaultvalue,
                                    attr.bcbs_config_attribute,
                                    attr.bcbs_config_masterattribute,
                                    null
                                );

                        if (!generateTiers) {
                            values.push({
                                id: uuidv4(),
                                tierRef: null,
                                type: 'primary',
                                value: defaultValue
                            });
                        } else {
                            tiers?.forEach(tier => {

                                if (attr.bcbs_config_attribute.bcbs_isexcludeoutofnetwork)
                                    if (tier.ion)
                                        return;

                                if (tier.pcp && generatePcpSpecialist ) {
                                    values.push({
                                        id: uuidv4(),
                                        tierRef: {
                                            id: tier.id,
                                            name: tier.name
                                        },
                                        type: 'pcp',
                                        value: defaultValue
                                    });
                                    values.push({
                                        id: uuidv4(),
                                        tierRef: {
                                            id: tier.id,
                                            name: tier.name
                                        },
                                        type: 'specialist',
                                        value: defaultValue
                                    });
                                } else {
                                    values.push({
                                        id: uuidv4(),
                                        tierRef: {
                                            id: tier.id,
                                            name: tier.name
                                        },
                                        type: 'primary',
                                        value: defaultValue
                                    });
                                }
                            });
                        }

                        fields.push({
                            configRef: {
                                id: attr.bcbs_config_attribute.bcbs_config_attributeid!,
                                name: attr.bcbs_config_attribute.bcbs_name!
                            },
                            id: uuidv4(),
                            masterAttributeID: attr.bcbs_config_attribute._bcbs_config_masterattributeid_value!,
                            name: attr.bcbs_config_attribute.bcbs_label || attr.bcbs_config_attribute.bcbs_name || attr.bcbs_config_masterattribute.bcbs_name || "",
                            values: values
                        });
                    }
                }
            }
        }

        return fields;
    }

    generateFieldsForCategoryId(categoryId: string, tiers?: ITierLevel[] | null): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.categoryFormDefinitions[categoryId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition, tiers);
    }

    generateAttributeSetFields(attributeSetId: string): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.attributeSetFormDefinitions[attributeSetId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition);
    }

    generateNonStandardFields(sectionId: string, tiers?: ITierLevel[]): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.nonStandardFormDefinitions[sectionId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition, tiers);
    }

    generateNonStandardTemplateFields(sectionId: string): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.nonStandardFormDefinitions[sectionId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition);
    }

    generateGroupConfigurationFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId($cat_$group_configuration_id);
    }

    generateBenefitFields(categoryId: string, tiers?: ITierLevel[] | null): IFlexibleField[] {
        return this.generateFieldsForCategoryId(categoryId, tiers);
    }

    generatePlanFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId($cat_$plan_id);
    }

    generateStopLossFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId($cat_$stop_loss_id);
    }

    generateFeesAndPremiumsFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId($cat_$fees_and_premiums_id);
    }

    generateAccountsReceivablesFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId($cat_$accounts_receivable_id);
    }

    generateGroupNumberFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId($cat_$group_number_id);
    }
}